import React, { useCallback, useMemo } from "react";
import { LazyQueryNetworkBoundrary } from "../../../components/mobile/QueryNetworkBoundrary";
import { useLazyLoadQuery, graphql } from "react-relay";
import { TaskTypesType } from "../../../types/TaskUIType";
import { RoutineEditScreenProps } from "../RoutineEditMain/RoutineEditNavigation";
import { RoutineEditScheduleDailyTaskImportanceQuery } from "./__generated__/RoutineEditScheduleDailyTaskImportanceQuery.graphql";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useGrid } from "./useGrid";
import { Box } from "arkh";
import Animated from "react-native-reanimated";
import { ScrollView } from "react-native";
import {
  ColumnItem,
  FIRST_COLUMN_WIDTH,
  FIRST_ROW_HEIGHT,
  RowItem,
} from "./components";
import { OccasionHeaderItem } from "./OccasionsHeaderRow";
import { TasksColumn } from "./TasksColumn";
import {
  useRoutineEditState,
  useRoutineEditStateSetParams,
} from "../State/RoutineEditState";

function toggleTaskOccasionRole(importance: "REQUIRED" | "OPTIONAL" | null) {
  switch (importance) {
    case "REQUIRED":
      return "OPTIONAL";
    case "OPTIONAL":
      return null;
    case null:
      return "REQUIRED";
  }
}
type ListItem = {
  weekday: number;
  startTime: {
    hour: number;
    minute: number;
  } | null;
  tasks: {
    taskId: string;
    importance: "REQUIRED" | "OPTIONAL" | null;
  }[];
};

function keyExtractor(item: ListItem) {
  return item.weekday + "w";
}

function headerKeyExtractor(weekday: ListItem) {
  return weekday.weekday + "";
}

function RoutineEditScheduleDailyTaskImportanceLoaded({
  id,

  fetchKey,
}: {
  fetchKey: string;
  id: string;
} & RoutineEditScreenProps<"RoutineEditScheduleDailyTaskImportance">) {
  const params = useRoutineEditState();
  const setParams = useRoutineEditStateSetParams();
  const data = useLazyLoadQuery<RoutineEditScheduleDailyTaskImportanceQuery>(
    graphql`
      query RoutineEditScheduleDailyTaskImportanceQuery($routineId: ID!) {
        routine(id: $routineId) {
          activeVersion {
            tasks {
              __typename
              id
              title
            }
          }
        }
      }
    `,
    { routineId: id },
    { fetchKey }
  );

  const remoteTaskIds = useMemo(
    () => data.routine?.activeVersion.tasks.map(({ id }) => id) ?? [],
    [data.routine?.activeVersion.tasks]
  );

  const tasks = useMemo(
    () =>
      params.tasks?.map((t) => ({
        title: t.title,
        id: t.id,
        __typename: (t.type === "TaskCheck"
          ? "TaskCheck"
          : t.type === "TaskNumber"
            ? "TaskNumber"
            : "TaskText") as TaskTypesType,
      })) ??
      data.routine?.activeVersion.tasks.map((t) => ({
        title: t.title,
        id: t.id,
        __typename: (t.__typename === "TaskCheck"
          ? "TaskCheck"
          : t.__typename === "TaskNumber"
            ? "TaskNumber"
            : "TaskText") as TaskTypesType,
      })) ??
      [],
    [data.routine?.activeVersion.tasks, params.tasks]
  );

  const onGridItemPress = useCallback(
    (
      occasionIndex: number,
      taskId: string,
      prevImportance: "REQUIRED" | "OPTIONAL" | null
    ) => {
      const prev = params.scheduleDaily;
      if (!prev) {
        return;
      }

      setParams({
        scheduleFreq: "daily",
        scheduleDaily: {
          ...prev,
          weekdays: prev.weekdays
            .sort((a, b) => a.weekday - b.weekday)
            .map((weekday, i) => {
              if (i === occasionIndex) {
                const taskImportanceChanges = weekday.taskImportanceChanges;
                const newImportance = toggleTaskOccasionRole(prevImportance);
                taskImportanceChanges[taskId] = newImportance;
                return {
                  ...weekday,
                  taskImportanceChanges,
                };
              }
              return weekday;
            }),
        },
      });
    },
    [params.scheduleDaily, setParams]
  );

  const insets = useSafeAreaInsets();
  const { body, header } = useGrid({ onGridItemPress });

  const onTaskPress = useCallback(
    (taskId: string) => {
      const prev = params.scheduleDaily;
      if (!prev) {
        return;
      }

      setParams({
        scheduleFreq: "daily",
        scheduleDaily: {
          ...prev,

          weekdays: prev?.weekdays.map((weekday) => ({
            ...weekday,
            taskImportanceChanges: {
              ...weekday.taskImportanceChanges,
              [taskId]: "REQUIRED",
            },
          })),
        },
      });
    },
    [params.scheduleDaily, setParams]
  );

  const weekdays = useMemo(
    () =>
      params.scheduleDaily?.weekdays
        .map((weekday) => ({
          weekday: weekday.weekday,
          startTime: weekday.startTime,
          tasks: tasks.map(({ id }) => ({
            taskId: id,
            importance:
              weekday.taskImportanceChanges[id] !== undefined
                ? weekday.taskImportanceChanges[id]
                : weekday.originalTaskImportance[id] !== undefined
                  ? weekday.originalTaskImportance[id]
                  : remoteTaskIds.includes(id)
                    ? null
                    : "REQUIRED",
          })),
        }))
        .slice()
        .sort((a, b) => a.weekday - b.weekday) ?? [],
    [params.scheduleDaily, tasks, remoteTaskIds]
  );

  return (
    <>
      <Box
        style={{
          flexDirection: "row",
          borderTopWidth: 1,
        }}
        borderColor="border-primary"
      >
        <Animated.FlatList
          ref={header.ref as any}
          horizontal
          scrollEventThrottle={1}
          bounces={false}
          onScroll={header.scrollHandler}
          showsHorizontalScrollIndicator={false}
          keyExtractor={headerKeyExtractor}
          contentContainerStyle={{ paddingLeft: FIRST_COLUMN_WIDTH }}
          renderItem={({ item }) => {
            return <OccasionHeaderItem weekday={item.weekday} />;
          }}
          data={weekdays}
        />
        <Box
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: FIRST_COLUMN_WIDTH,
            height: FIRST_ROW_HEIGHT,
          }}
        >
          <ColumnItem first color="background" style={{ opacity: 0.6 }}>
            <RowItem first />
          </ColumnItem>
        </Box>
      </Box>
      <ScrollView
        style={{ flex: 1 }}
        nestedScrollEnabled
        contentContainerStyle={{
          flexDirection: "row",
          paddingBottom: insets.bottom,
        }}
        showsHorizontalScrollIndicator={false}
      >
        <ColumnItem first>
          <TasksColumn tasks={tasks} onPress={onTaskPress} />
        </ColumnItem>
        <Animated.FlatList
          ref={body.ref as any}
          onScroll={body.scrollHandler}
          data={weekdays}
          bounces={false}
          horizontal
          showsHorizontalScrollIndicator={false}
          scrollEventThrottle={1}
          snapToInterval={100}
          initialNumToRender={8}
          keyExtractor={keyExtractor}
          style={{ flex: 1 }}
          getItemLayout={body.getItemLayout}
          ListFooterComponent={body.footer}
          renderItem={body.renderGridItem}
        />
      </ScrollView>
    </>
  );
}

export function RoutineEditScheduleDailyTaskImportance(
  props: RoutineEditScreenProps<"RoutineEditScheduleDailyTaskImportance">
) {
  return (
    <LazyQueryNetworkBoundrary>
      {(fetchKey) => (
        <RoutineEditScheduleDailyTaskImportanceLoaded
          {...props}
          id={props.route.params.id}
          fetchKey={fetchKey}
        />
      )}
    </LazyQueryNetworkBoundrary>
  );
}
